/**
 * "Typ" — a training or a consultation, as the canon's two cards (T-A4): the kind's own hue on its
 * tile (the grid's hue for that kind, so the card and the block it makes agree), its name, and the
 * length it starts with. A native radio group underneath, so the arrows move between the two.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { ConsultationIcon } from '~/shared/ui/CalendarEventIcons';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import type { SessionKind } from '../model/bookingSession';
import { defaultMinutes, type DraftPolicy } from '../model/sessionDraft';
import { TrainingGlyph } from './BookingGlyphs';
import styles from './ComposeForm.module.css';

const KINDS: readonly SessionKind[] = ['training', 'consultation'];

interface KindChoiceProps {
  readonly kind: SessionKind;
  readonly policy: DraftPolicy;
  readonly onChange: (kind: SessionKind) => void;
}

export function KindChoice({ kind, policy, onChange }: KindChoiceProps) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.label}>{t('calendar.create.typeLabel')}</legend>
      <div className={styles.kinds}>
        {KINDS.map((entry) => (
          <label
            key={entry}
            className={`${String(styles.kind)} ${entry === kind ? String(styles.kindChosen) : ''}`}
          >
            <input
              className={hiddenControl.control}
              type="radio"
              name={name}
              value={entry}
              checked={entry === kind}
              onChange={() => {
                onChange(entry);
              }}
            />
            <span
              className={`${String(styles.kindTile)} ${entry === 'consultation' ? String(styles.kindTileConsultation) : ''}`}
              aria-hidden="true"
            >
              {entry === 'consultation' ? (
                <ConsultationIcon className={styles.kindGlyph} />
              ) : (
                <TrainingGlyph className={styles.kindGlyph} />
              )}
            </span>
            <span className={styles.kindText}>
              <span className={styles.kindName}>
                {t(
                  entry === 'consultation'
                    ? 'calendar.create.type.consultation'
                    : 'calendar.create.type.workout',
                )}
              </span>
              <span className={styles.kindMeta}>
                {t('web:booking.create.kindMeta', { minutes: defaultMinutes(entry, policy) })}
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
